import React from "react";
import { Skeleton } from "antd";
import { VictoryPie } from "victory";
import "./InsightsDonutCard.less";
import type { InsightsDonutCardProps } from "./types";

export type { InsightsDonutSegment, InsightsDonutCardProps } from "./types";

const DEFAULT_SIZE = 140;
const DEFAULT_EMPTY_STATE_COLOR = "#E5E7EB";
/**
 * inner / outer radius, measured from the Figma donut's exported ring-segment
 * SVG path: center (48, 48), outer radius 48, inner radius 33.68 → 33.68/48.
 */
const DEFAULT_INNER_RADIUS_RATIO = 33.68 / 48;

/**
 * Generic, data-driven donut chart card: a title, a centered total, a ring
 * chart and a colored legend. Renders purely from `segments` — callers own
 * all domain-specific labels/colors/ordering, so this component has no
 * knowledge of what it's charting and can be reused for any status/count
 * breakdown.
 */
export const InsightsDonutCard: React.FC<InsightsDonutCardProps> = ({
  id,
  title,
  total,
  segments,
  loading = false,
  size = DEFAULT_SIZE,
  innerRadius = (size / 2) * DEFAULT_INNER_RADIUS_RATIO,
  emptyStateColor = DEFAULT_EMPTY_STATE_COLOR,
  error = false,
  errorMessage,
  onRetry,
  retryLabel,
  className,
}) => {
  const isEmpty = total === 0;
  // Zero-value segments (e.g. "Draft: 0") stay in the legend but must not get
  // a slot in the pie itself — VictoryPie's padAngle still inserts a gap
  // around a 0-degree slice, which shows up as a visible notch in an
  // otherwise-solid ring (e.g. 100% Published, 0% Draft).
  const nonZeroSegments = segments.filter((segment) => segment.value > 0);

  const pieData = isEmpty
    ? [{ x: id, y: 1 }]
    : nonZeroSegments.map((segment) => ({ x: segment.id, y: segment.value }));

  const colorScale = isEmpty
    ? [emptyStateColor]
    : nonZeroSegments.map((segment) => segment.color);

  const rootClassName = ["insights-donut-card", className]
    .filter(Boolean)
    .join(" ");

  if (error) {
    return (
      <div className={rootClassName} data-testid={`insights-donut-card-${id}`}>
        <span className="insights-donut-card__title">{title}</span>

        <div
          className="insights-donut-card__error"
          data-testid={`insights-donut-card-${id}-error`}
        >
          <span className="insights-donut-card__error-text">
            {errorMessage}
          </span>
          {onRetry && (
            <button
              type="button"
              className="insights-donut-card__retry"
              onClick={onRetry}
            >
              {retryLabel}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={rootClassName} data-testid={`insights-donut-card-${id}`}>
      <span className="insights-donut-card__title">{title}</span>

      <div className="insights-donut-card__body">
        <div
          className="insights-donut-card__chart"
          style={{ width: size, height: size }}
        >
          {loading ? (
            // antd's Skeleton.Avatar doesn't forward unknown DOM props (e.g.
            // data-testid), so it's wrapped for that hook instead.
            <div data-testid={`insights-donut-card-${id}-chart-skeleton`}>
              <Skeleton.Avatar active shape="circle" size={size} />
            </div>
          ) : (
            <>
              <VictoryPie
                data={pieData}
                innerRadius={innerRadius}
                padAngle={nonZeroSegments.length > 1 ? 1 : 0}
                labels={() => null}
                colorScale={colorScale}
                width={size}
                height={size}
                padding={0}
              />
              <span className="insights-donut-card__total">{total}</span>
            </>
          )}
        </div>

        {loading ? (
          <div
            className="insights-donut-card__legend"
            data-testid={`insights-donut-card-${id}-legend-skeleton`}
          >
            <Skeleton active title={false} paragraph={{ rows: 3 }} />
          </div>
        ) : (
          <ul className="insights-donut-card__legend">
            {segments.map((segment) => (
              <li key={segment.id} className="insights-donut-card__legend-row">
                <span
                  className="insights-donut-card__legend-dot"
                  style={{ backgroundColor: segment.color }}
                />
                <span className="insights-donut-card__legend-label">
                  {segment.label}
                </span>
                <span className="insights-donut-card__legend-value">
                  {segment.value}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default InsightsDonutCard;
